import React, { useMemo } from 'react';
import {
    combineLocalDateTime,
    splitLocalDateTime,
    toDateInput
} from '../../../../utils/date-time';
import styles from './AdminDateTimeInput.module.scss';

interface AdminDateTimeInputProps {
    id: string;
    value?: string | null;
    onChange: (value: string) => void;
    includeTime?: boolean;
    disabled?: boolean;
    required?: boolean;
    clearable?: boolean;
}

const AdminDateTimeInput = ({
    id,
    value,
    onChange,
    includeTime = false,
    disabled = false,
    required = false,
    clearable = true
}: AdminDateTimeInputProps) => {
    const normalized = useMemo(() => {
        if (includeTime) {
            return splitLocalDateTime(value);
        }

        return {
            date: toDateInput(value),
            time: ''
        };
    }, [includeTime, value]);

    const dateValue = normalized.date;
    const timeValue = normalized.time || '00:00';

    return (
        <div className={styles.root}>
            <input
                id={id}
                type="date"
                className={styles.dateInput}
                value={dateValue}
                disabled={disabled}
                required={required}
                onChange={(event) => {
                    const nextDate = event.target.value;
                    if (!includeTime) {
                        onChange(nextDate);
                        return;
                    }

                    onChange(nextDate ? combineLocalDateTime(nextDate, normalized.time || '00:00') : '');
                }}
            />
            {includeTime ? (
                <input
                    id={`${id}-time`}
                    type="time"
                    className={styles.timeInput}
                    value={dateValue ? timeValue : ''}
                    disabled={disabled || !dateValue}
                    onChange={(event) => {
                        if (!dateValue) {
                            return;
                        }

                        onChange(combineLocalDateTime(dateValue, event.target.value || '00:00'));
                    }}
                />
            ) : null}
            {clearable ? (
                <button
                    type="button"
                    className={styles.clearButton}
                    disabled={disabled || !dateValue}
                    onClick={() => onChange('')}
                >
                    Clear
                </button>
            ) : null}
        </div>
    );
};

export default AdminDateTimeInput;
